import React, { type FC, type PropsWithChildren, useEffect } from 'react';

import ABTestContext from '../context/ab-test-context-singular';
import useAbTestInit, { type ABTestProps } from '../hooks/use-ab-test-init';
import ABTestShield from './ab-test-shield';
import type { Lucid } from '../../../types/types';

export type ABTestBaseProps = ABTestProps & {
  /** Used to pass the test data up to components where the AB Test context is out of scope */
  setExternalTestData?: React.Dispatch<React.SetStateAction<Lucid.Context.ABTest.Props>>;
};

/**
 * Base component for A/B testing in marketing sites.
 *
 * @remarks
 * Initializes and provides A/B test context to its children.
 * Optionally, it can expose the selected branch via a setter for use outside the context.
 *
 * @returns
 * Provides A/B test context and optionally renders children within a styled box.
 *
 * @example
 * ```tsx
 * const [currentTestData, setCurrentTestData] = useState<Lucid.ABTest.VariationKeys>('T_A');
 * ---
 * <ABTesting.Component.Base
 *   branchesToRender={['T_A', 'T_B']}
 *   testName="example-test"
 *   isTestMode={true}
 *   setExternalTestData={setCurrentTestData}
 * />
 * ```
 *
 * @example
 * ```tsx
 * <ABTesting.Component.Base
 *   branchesToRender={['T_A', 'T_B']}
 *   testName="example-test"
 *   isTestMode={true}
 * >
 *  <ABTesting.Component.Branch branch="T_A" />
 *  <ABTesting.Component.Branch branch="T_B" />
 * </ABTesting.Component.Base>
 * ```
 */
const ABTestBase: FC<PropsWithChildren<ABTestBaseProps>> = ({
  branchesToRender = [] as Lucid.ABTest.VariationKeys[],
  children = null,
  isAboveTheFold = false,
  isTestMode = false,
  ssrBranch,
  testName,
  trigger = 'load',
  willTriggerFire = true,
  setExternalTestData,
}) => {
  const abTestContext = useAbTestInit({
    branchesToRender,
    isAboveTheFold,
    isTestMode,
    ssrBranch,
    testName,
    trigger,
    willTriggerFire,
  });

  useEffect(() => {
    if (setExternalTestData && willTriggerFire) {
      setExternalTestData(abTestContext);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abTestContext.branch]);

  return (
    <ABTestContext.Provider value={abTestContext}>
      <ABTestShield shouldShowShield={abTestContext?.shouldShowShield || false} />
      {children}
    </ABTestContext.Provider>
  );
};

export { ABTestBase };
export default ABTestBase;
